import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Row, Col } from "../../components/ui/wrapper";
import Form, { FormGroup, Input, Error } from "../ui/form";
import Button from "../ui/button";
import Heading from "../ui/heading";
import { ApplyJobFormContainer } from "./careers.style";
import Turnstile from "react-turnstile";
import {
  JobApplyFormSuccessMessage,
  JobApplySubmittingMessage,
  JobApplyFormErrorMessage,
} from "./JobApplyFormStatusMessage";
import axios from "axios";
import { JSPositionFormFields } from "./JSPositionFormFields";

// Set to false to disable API calls and show fake success message
const IS_API_ENABLED = false;

const ApplyJobForm = ({
  pageContext,
  location,
  slug,
  customFields,
  ...restProps
}) => {
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [isFormSubmitted, setFormSubmitted] = useState(false);
  const [hasSubmitErrors, setSubmitErrors] = useState(false);
  const [isFormSubmitting, setFormSubmitting] = useState(false);
  const { hasJSFields } = customFields;

  //console.log("TURNSTILE_SITE_KEY", process.env.TURNSTILE_SITE_KEY);
  const { headingStyle, btnStyle } = restProps;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
  });

  const resetStatuses = () => {
    setFormSubmitted(false);
    setSubmitErrors(false);
    setFormSubmitting(false);
  };

  const handleErrorStatuses = () => {
    setFormSubmitting(false);
    setSubmitErrors(true);
    setFormSubmitted(false);
  }

  const onSubmit = async ({ resume, ...data }) => {
    if (!turnstileToken) {
      alert("Please verify you are human");
      handleErrorStatuses();
      return;
    }

    // If API is disabled, show success message without making any API calls
    if (!IS_API_ENABLED) {
      setFormSubmitted(true);
      return;
    }

    setFormSubmitting(true);
    const selectedFile = resume;
    const candidateName = data?.name;

    try {
      const uploadedFileName = await uploadToR2(selectedFile[0], candidateName);

      const formData = {
        ...data,
        uploadedFileName,
        turnstileToken,
      };

      const formDataStr = JSON.stringify(formData);

      fetch("/api/careers", {
        method: "post",
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
        },
        body: formDataStr,
      })
        .then((res) => {
          setFormSubmitting(false);
          res.status === 200 || res.status === 202
            ? setFormSubmitted(true)
            : setFormSubmitted(false);
          reset();
        })
        .catch((error) => {
          setFormSubmitting(false);
          console.error("Error:", error);
          alert(error);
        });
    } catch (error) {
      handleErrorStatuses();
    }
  };

  const uploadToR2 = async (selectedFile, candidateName) => {
    const url = "/api/files/uploadUrl";

    const randomFilePrefix = Math.floor(Math.random() * 100);
    const fileExtension = selectedFile?.name?.split(".")?.pop();
    const fileName = candidateName?.split(" ")?.join("_");
    // Ensure the file is uploaded to the website/ folder
    const uploadedFileName = `website/${fileName}_${randomFilePrefix}.${fileExtension}`;

    const uploadUrlResponse = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ fileName: uploadedFileName }),
    });
    const uploadUrlResponseData = await uploadUrlResponse.json();

    const uploadUrl = uploadUrlResponseData.uploadUrl;

    const headers = {
      "Content-Type": selectedFile.type || "application/octet-stream",
    };

    const response = await axios.put(uploadUrl, selectedFile, {
      headers,
      onUploadProgress: ({ loaded, total }) => {
        const totalProgress = parseInt((loaded / total) * 100);
        console.log(`Progress - ${totalProgress}%`);
      },
    });

    console.log(`Uploaded: ${uploadedFileName}`);
    return uploadedFileName;
  };

  if (isFormSubmitting) {
    return <JobApplySubmittingMessage />;
  }

  if (isFormSubmitted) {
    return <JobApplyFormSuccessMessage />;
  }

  if (hasSubmitErrors) {
    return <JobApplyFormErrorMessage resetStatuses={resetStatuses} />
  }

  return (
    <ApplyJobFormContainer>
      <Heading mb="20px" {...headingStyle}>
        Apply Now:
      </Heading>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Input
          type="hidden"
          {...register("jobSlug", { required: "Slug is required" })}
          id="jobSlug"
          value={slug}
        />
        <Row gutters={{ lg: 20 }}>
          <Col md={6}>
            <FormGroup mb="20px">
              <Input
                type="text"
                {...register("name", {
                  required: "Your first and last name is required",
                })}
                id="name"
                placeholder="Name *"
              />
              <Error>{errors?.name && errors?.name?.message}</Error>
            </FormGroup>
          </Col>
        </Row>

        {hasJSFields && <JSPositionFormFields register={register} />}

        <Row gutters={{ md: 20 }}>
          <Col md={6}>
            <FormGroup mb="20px">
              <Input
                type="email"
                {...register("email", {
                  required: "Your email address is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                    message: "Invalid email address",
                  },
                })}
                id="email"
                placeholder="Email *"
              />
              <Error>{errors?.email && errors?.email?.message}</Error>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup mb="20px">
              <Input
                type="phone"
                {...register("phone", {
                  required: "Your phone number is required",
                  pattern: {
                    value: /^[0-9]+/,
                    message: "Invalid phone",
                  },
                })}
                id="phone"
                placeholder="Phone *"
              />
              <Error>{errors?.phone && errors?.phone?.message}</Error>
            </FormGroup>
          </Col>
        </Row>
        <Row gutters={{ md: 20 }}>
          <Col md={6}>
            <FormGroup mb="20px">
              <Input
                type="float"
                {...register("currentSalary", {
                  required: "Your annual current salary is required",
                })}
                id="currentSalary"
                placeholder="Annual Current Salary (INR) *"
              />
              <Error>
                {errors?.currentSalary && errors?.currentSalary?.message}
              </Error>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup mb="20px">
              <Input
                type="float"
                {...register("expectedSalary", {
                  required: "Your annual expected salary is required",
                })}
                id="expectedSalary"
                placeholder="Annual Expected Salary (INR) *"
              />
              <Error>
                {errors?.expectedSalary && errors?.expectedSalary?.message}
              </Error>
            </FormGroup>
          </Col>
        </Row>
        <Row gutters={{ md: 20 }}>
          <Col md={6}>
            <FormGroup mb="20px">
              <Input
                type="text"
                {...register("noticeperiod", {
                  required: "Your Notice period (In Months) is required",
                })}
                id="noticeperiod"
                placeholder="Notice Period (In Months) *"
              />
              <Error>
                {errors?.noticeperiod && errors?.noticeperiod?.message}
              </Error>
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup mb="20px">
              <Input
                type="text"
                {...register("residencelocation", {
                  required: "Specify the locality where you currently stay",
                })}
                id="residencelocation"
                placeholder="Residence Locality *"
              />
              <Error>
                {errors?.residencelocation &&
                  errors?.residencelocation?.message}
              </Error>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <FormGroup mb="20px">
              <Input
                pt="10px"
                type="file"
                {...register("resume", {
                  required: "Please upload your resume",
                })}
                id="resume"
                placeholder="Attach Resume *"
              />
              <Error>{errors?.resume && errors?.resume?.message}</Error>
            </FormGroup>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <div style={{ margin: "20px 0" }}>
              <Turnstile
                sitekey={process.env.TURNSTILE_SITE_KEY}
                onVerify={(token) => setTurnstileToken(token)}
              />
            </div>
            <Button type="submit" {...btnStyle}>
              Apply Now
            </Button>
          </Col>
        </Row>
      </Form>
    </ApplyJobFormContainer>
  );
};

ApplyJobForm.defaultProps = {
  headingStyle: {
    fontSize: "26px",
    lineHeight: "36px",
    fontweight: 400,
    padding: "10px",
    pb: "10px",
  },
  btnStyle: {
    mt: "10px",
    skin: "secondary",
    minwidth: "100px",
    hover: {
      bgColor: "primary",
    },
  },
};

ApplyJobForm.propTypes = {};

export default ApplyJobForm;
